import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  ClerkProvider,
  SignIn,
  SignUp,
  Show,
  useAuth,
  useClerk,
} from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { shadcn } from "@clerk/themes";
import {
  Switch,
  Route,
  useLocation,
  Router as WouterRouter,
  Redirect,
} from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import {
  getGetMeQueryKey,
  setAuthTokenGetter,
  useGetMe,
} from "@workspace/api-client-react";
import { useApplyReferral } from "./hooks/useApplyReferral";
import { AppShell } from "./components/AppShell";
import { AgentOnboardingGate } from "./components/AgentOnboardingGate";
import { AccountActivationGate } from "./components/AccountActivationGate";
import { AccountActivate } from "./pages/AccountActivate";
import { CompleteProfile } from "./pages/CompleteProfile";
import { HomeRouter } from "./pages/HomeRouter";
import { OnboardingWizard } from "./pages/onboarding/OnboardingWizard";
import { OnboardingStatus } from "./pages/onboarding/OnboardingStatus";
import { LmsTrainingModule } from "./pages/training/LmsTrainingModule";
import { InviteAccept } from "./pages/InviteAccept";
import { JoinPage } from "./pages/JoinPage";
import { Landing } from "./pages/Landing";
import { Settings } from "./pages/Settings";
import { AgentProfile } from "./pages/shared/AgentProfile";
import { LeadsExplorer } from "./pages/shared/LeadsExplorer";
import { MarketingMaterials } from "./pages/shared/MarketingMaterials";

// Agent pages
import { OrdersList } from "./pages/agent/OrdersList";
import { WalletView } from "./pages/agent/WalletView";
import { SupportList } from "./pages/agent/SupportList";

// Master Dealer pages
import { AgentsList } from "./pages/master-dealer/AgentsList";
import { InvitationsList } from "./pages/master-dealer/InvitationsList";
import { InfoSessionsList } from "./pages/master-dealer/InfoSessionsList";

// Account Manager pages
import { RegionsList } from "./pages/account-manager/RegionsList";
import { TicketsList } from "./pages/account-manager/TicketsList";
import { OnboardingReviewsList } from "./pages/account-manager/OnboardingReviewsList";

// Admin pages
import { ApplicationsList } from "./pages/admin/ApplicationsList";
import { MasterDealersList } from "./pages/admin/MasterDealersList";
import { AccountManagersList } from "./pages/admin/AccountManagersList";
import { AuditLogList } from "./pages/admin/AuditLogList";
import { DataImportsList } from "./pages/admin/DataImportsList";
import { OnboardingFunnel } from "./pages/admin/OnboardingFunnel";
import { UsersRolesList } from "./pages/admin/UsersRolesList";
import { UserManagement } from "./pages/admin/UserManagement";
import { DataImport } from "./pages/operations/DataImport";
import { Commission } from "./pages/operations/Commission";

// Finance pages
import { PaymentRunsList } from "./pages/finance/PaymentRunsList";
import { PaymentRunDetail } from "./pages/finance/PaymentRunDetail";
import { TransactionsList } from "./pages/finance/TransactionsList";
import { BankChangesList } from "./pages/finance/BankChangesList";

// Support pages
import { DisputesList } from "./pages/support/DisputesList";
import { DisputePublicReport } from "./pages/support/DisputePublicReport";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL || undefined;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY in .env file");
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/mooya-logo.png`,
  },
  variables: {
    colorPrimary: "hsl(327, 100%, 45%)",
    colorForeground: "hsl(225, 60%, 11%)",
    colorBackground: "hsl(0, 0%, 100%)",
    fontFamily: "'Inter', sans-serif",
    borderRadius: "0.5rem",
  },
  elements: {
    formButtonPrimary: "bg-[#E6007E] hover:bg-[#C5006C] text-white",
  },
};

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4 py-12">
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
      />
    </div>
  );
}

function SignUpPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-slate-50 px-4 py-12">
      <SignUp
        routing="path"
        path={`${basePath}/sign-up`}
        signInUrl={`${basePath}/sign-in`}
      />
    </div>
  );
}

function HomeRedirect() {
  return (
    <>
      <Show when="signed-in">
        <Redirect to="/home" />
      </Show>
      <Show when="signed-out">
        <Landing />
      </Show>
    </>
  );
}

type Role =
  | "agent"
  | "master_dealer"
  | "account_manager"
  | "admin"
  | "finance"
  | "poc_support"
  | "operations";

function ProtectedRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  return (
    <>
      <Show when="signed-in">
        <AccountActivationGate>
          <AgentOnboardingGate>
            <AppShell>
              <Component />
            </AppShell>
          </AgentOnboardingGate>
        </AccountActivationGate>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function FallbackComponent() {
  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-2xl font-semibold text-slate-800">
        Something went wrong
      </h1>
      <p className="text-slate-500">
        An unexpected error occurred. Please refresh the page to try again.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 bg-[#E6007E] text-white rounded-md hover:bg-[#C5006C]"
      >
        Refresh page
      </button>
    </div>
  );
}

// Backend authorization is the source of truth; this gate keeps the UX
// consistent by redirecting users away from screens their role can't access.
function RoleGate({
  roles,
  children,
}: {
  roles: Role[];
  children: React.ReactNode;
}) {
  const { data: me, isLoading } = useGetMe();
  if (isLoading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center text-slate-400">
        Loading…
      </div>
    );
  }
  if (!me || !roles.includes(me.role as Role)) {
    return <Redirect to="/home" />;
  }
  return <>{children}</>;
}

function RoleProtectedRoute({
  component: Component,
  roles,
}: {
  component: React.ComponentType;
  roles: Role[];
}) {
  return (
    <>
      <Show when="signed-in">
        <AccountActivationGate>
          <AgentOnboardingGate>
            <RoleGate roles={roles}>
              <AppShell>
                <Component />
              </AppShell>
            </RoleGate>
          </AgentOnboardingGate>
        </AccountActivationGate>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClientLocal = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    return addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClientLocal.clear();
      }
      prevUserIdRef.current = userId;
    });
  }, [addListener, queryClientLocal]);

  useApplyReferral();
  return null;
}

function ClerkApiAuthBridge() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const queryClientLocal = useQueryClient();

  useEffect(() => {
    if (!isLoaded) return;

    setAuthTokenGetter(() =>
      isSignedIn ? getToken() : Promise.resolve(null),
    );

    if (isSignedIn) {
      queryClientLocal.invalidateQueries({ queryKey: getGetMeQueryKey() });
    }

    return () => setAuthTokenGetter(null);
  }, [getToken, isLoaded, isSignedIn, queryClientLocal]);

  return null;
}

function App() {
  const [, setLocation] = useLocation();
  return (
    <Sentry.ErrorBoundary fallback={<FallbackComponent />}>
    <WouterRouter base={basePath}>
      <ClerkProvider
        publishableKey={clerkPubKey}
        proxyUrl={clerkProxyUrl}
        appearance={clerkAppearance}
        signInUrl={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        routerPush={(to) => setLocation(stripBase(to))}
        routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
      >
        <QueryClientProvider client={queryClient}>
          <ClerkQueryClientCacheInvalidator />
          <ClerkApiAuthBridge />
          <TooltipProvider>
            <Switch>
              <Route path="/" component={HomeRedirect} />
              <Route path="/invite" component={InviteAccept} />
              <Route path="/join" component={JoinPage} />
              <Route
                path="/disputes/public-report"
                component={DisputePublicReport}
              />
              <Route path="/sign-in/*?" component={SignInPage} />
              <Route path="/sign-up/*?" component={SignUpPage} />

              {/* Account activation + profile completion are full-screen and
                  signed-in only. They are intentionally NOT behind
                  AccountActivationGate — they are the gate's redirect targets,
                  and each guards its own state internally. */}
              <Route
                path="/activate"
                component={() => (
                  <>
                    <Show when="signed-in">
                      <AccountActivate />
                    </Show>
                    <Show when="signed-out">
                      <Redirect to="/" />
                    </Show>
                  </>
                )}
              />
              <Route
                path="/complete-profile"
                component={() => (
                  <>
                    <Show when="signed-in">
                      <CompleteProfile />
                    </Show>
                    <Show when="signed-out">
                      <Redirect to="/" />
                    </Show>
                  </>
                )}
              />

              {/* Onboarding is full-screen (no AppShell). The wizard and status
                  page guard their own state transitions, but the account must be
                  activated first, so they sit behind AccountActivationGate. */}
              <Route
                path="/onboarding/status"
                component={() => (
                  <>
                    <Show when="signed-in">
                      <AccountActivationGate>
                        <OnboardingStatus />
                      </AccountActivationGate>
                    </Show>
                    <Show when="signed-out">
                      <Redirect to="/" />
                    </Show>
                  </>
                )}
              />
              <Route
                path="/onboarding"
                component={() => (
                  <>
                    <Show when="signed-in">
                      <AccountActivationGate>
                        <OnboardingWizard />
                      </AccountActivationGate>
                    </Show>
                    <Show when="signed-out">
                      <Redirect to="/" />
                    </Show>
                  </>
                )}
              />

              {/* LMS training is self-service and full-screen. It is NOT behind
                  the agent onboarding gate so an agent awaiting approval can
                  complete training in parallel, but the account must still be
                  activated first. */}
              <Route
                path="/training"
                component={() => (
                  <>
                    <Show when="signed-in">
                      <AccountActivationGate>
                        <LmsTrainingModule />
                      </AccountActivationGate>
                    </Show>
                    <Show when="signed-out">
                      <Redirect to="/" />
                    </Show>
                  </>
                )}
              />

              <Route
                path="/home"
                component={() => <ProtectedRoute component={HomeRouter} />}
              />

              {/* Agent routes */}
              <Route
                path="/orders"
                component={() => (
                  <RoleProtectedRoute
                    component={OrdersList}
                    roles={[
                      "agent",
                      "master_dealer",
                      "account_manager",
                      "admin",
                    ]}
                  />
                )}
              />
              <Route
                path="/leads"
                component={() => (
                  <RoleProtectedRoute
                    component={LeadsExplorer}
                    roles={[
                      "master_dealer",
                      "account_manager",
                      "admin",
                      "finance",
                      "poc_support",
                      "operations",
                    ]}
                  />
                )}
              />
              <Route
                path="/marketing"
                component={() => (
                  <RoleProtectedRoute
                    component={MarketingMaterials}
                    roles={[
                      "agent",
                      "master_dealer",
                      "account_manager",
                      "admin",
                      "finance",
                      "poc_support",
                      "operations",
                    ]}
                  />
                )}
              />
              <Route
                path="/wallet"
                component={() => (
                  <RoleProtectedRoute
                    component={WalletView}
                    roles={["agent", "admin"]}
                  />
                )}
              />
              <Route
                path="/support"
                component={() => (
                  <RoleProtectedRoute
                    component={SupportList}
                    roles={["agent"]}
                  />
                )}
              />

              {/* Shared routes */}
              <Route
                path="/settings"
                component={() => <ProtectedRoute component={Settings} />}
              />

              {/* MD routes */}
              <Route
                path="/agents/:id"
                component={() => (
                  <RoleProtectedRoute
                    component={AgentProfile}
                    roles={[
                      "master_dealer",
                      "account_manager",
                      "admin",
                      "operations",
                      "poc_support",
                    ]}
                  />
                )}
              />
              <Route
                path="/agents"
                component={() => (
                  <RoleProtectedRoute
                    component={AgentsList}
                    roles={[
                      "master_dealer",
                      "account_manager",
                      "admin",
                      "operations",
                      "poc_support",
                    ]}
                  />
                )}
              />
              <Route
                path="/invitations"
                component={() => (
                  <RoleProtectedRoute
                    component={InvitationsList}
                    roles={["master_dealer", "account_manager", "admin"]}
                  />
                )}
              />
              <Route
                path="/info-sessions"
                component={() => (
                  <RoleProtectedRoute
                    component={InfoSessionsList}
                    roles={["master_dealer", "account_manager", "admin"]}
                  />
                )}
              />

              {/* AM routes */}
              <Route
                path="/onboarding-reviews"
                component={() => (
                  <RoleProtectedRoute
                    component={OnboardingReviewsList}
                    roles={["account_manager", "admin", "operations"]}
                  />
                )}
              />
              <Route
                path="/regions"
                component={() => (
                  <RoleProtectedRoute
                    component={RegionsList}
                    roles={["account_manager", "admin"]}
                  />
                )}
              />
              <Route
                path="/tickets"
                component={() => (
                  <RoleProtectedRoute
                    component={TicketsList}
                    roles={["agent", "poc_support", "account_manager", "admin"]}
                  />
                )}
              />

              {/* Admin routes */}
              <Route
                path="/applications"
                component={() => (
                  <RoleProtectedRoute
                    component={ApplicationsList}
                    roles={[
                      "master_dealer",
                      "account_manager",
                      "admin",
                      "operations",
                    ]}
                  />
                )}
              />
              <Route
                path="/master-dealers"
                component={() => (
                  <RoleProtectedRoute
                    component={MasterDealersList}
                    roles={["account_manager", "admin"]}
                  />
                )}
              />
              <Route
                path="/account-managers"
                component={() => (
                  <RoleProtectedRoute
                    component={AccountManagersList}
                    roles={["admin"]}
                  />
                )}
              />
              <Route
                path="/users"
                component={() => (
                  <RoleProtectedRoute
                    component={UsersRolesList}
                    roles={["admin"]}
                  />
                )}
              />
              <Route
                path="/staff"
                component={() => (
                  <RoleProtectedRoute
                    component={UserManagement}
                    roles={["admin"]}
                  />
                )}
              />
              <Route
                path="/audit-log"
                component={() => (
                  <RoleProtectedRoute
                    component={AuditLogList}
                    roles={["admin"]}
                  />
                )}
              />
              <Route
                path="/data-imports"
                component={() => (
                  <RoleProtectedRoute
                    component={DataImportsList}
                    roles={["admin", "finance"]}
                  />
                )}
              />
              <Route
                path="/onboarding-funnel"
                component={() => (
                  <RoleProtectedRoute
                    component={OnboardingFunnel}
                    roles={["admin", "account_manager"]}
                  />
                )}
              />

              {/* Operations routes (Mooya staff only) */}
              <Route
                path="/data-import"
                component={() => (
                  <RoleProtectedRoute
                    component={DataImport}
                    roles={["admin", "finance"]}
                  />
                )}
              />
              <Route
                path="/commission"
                component={() => (
                  <RoleProtectedRoute
                    component={Commission}
                    roles={["admin", "account_manager"]}
                  />
                )}
              />

              {/* Finance routes */}
              <Route
                path="/payment-runs"
                component={() => (
                  <RoleProtectedRoute
                    component={PaymentRunsList}
                    roles={["finance", "admin"]}
                  />
                )}
              />
              <Route
                path="/payment-runs/:id"
                component={() => (
                  <RoleProtectedRoute
                    component={PaymentRunDetail}
                    roles={["finance", "admin"]}
                  />
                )}
              />
              <Route
                path="/transactions"
                component={() => (
                  <RoleProtectedRoute
                    component={TransactionsList}
                    roles={["agent", "finance", "admin"]}
                  />
                )}
              />
              <Route
                path="/bank-changes"
                component={() => (
                  <RoleProtectedRoute
                    component={BankChangesList}
                    roles={["agent", "finance", "admin"]}
                  />
                )}
              />

              {/* Support routes */}
              <Route
                path="/disputes"
                component={() => (
                  <RoleProtectedRoute
                    component={DisputesList}
                    roles={["agent", "poc_support", "finance", "admin"]}
                  />
                )}
              />

              {/* Fallback */}
              <Route
                path="/:rest*"
                component={() => (
                  <ProtectedRoute
                    component={() => (
                      <div className="p-8 text-center text-slate-500">
                        Page under construction
                      </div>
                    )}
                  />
                )}
              />
            </Switch>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </WouterRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
